import { ColDef, ITooltipParams, ValueFormatterParams } from 'ag-grid-enterprise';
import { filterParams } from 'features/Remittance/reportsListConfig';
import { formatDate } from 'utilities/dateFormatter';
import { OrderStatus, PartyRow, PartyVerificationListResult } from './interfaces/partyList';

export const normalizeOrderStatus = (status?: OrderStatus): OrderStatus | undefined =>
  status?.toLowerCase() === OrderStatus.USER_CANCELLED.toLowerCase() ? OrderStatus.REQUEST_CANCELLED : status;

export const normalizePartyRows = (result: PartyVerificationListResult[]): PartyRow[] =>
  (result ?? []).map((r) => {
    const cvDate = r.contactValidationOrdersSummary?.lastStatusDate;
    const idvDate = r.identityVerificationOrdersSummary?.lastStatusDate;

    const lastRun = [cvDate, idvDate].filter(Boolean).sort((a, b) => (a! > b! ? -1 : 1))[0] ?? undefined;

    return {
      fileId: r.fileId,
      fileNumber: r.fileNumber,
      underwriter: r.underwriterName || r.underwriterCode,
      partyName: r.partyName,
      requestedByName: r.requestedByName,

      cvStatus: r.contactValidationOrdersSummary?.lastStatus,
      cvDate,

      idvStatus: normalizeOrderStatus(r.identityVerificationOrdersSummary?.lastStatus),
      idvDate,
      idvAttemptsLeft: r.identityVerificationOrdersSummary?.attemptRemaining,
      idvOrderId: r.identityVerificationOrdersSummary?.lastOrderId,

      lastRun,
    };
  });

const fmtDate = (p: ValueFormatterParams) =>
  p.value ? formatDate(p.value, '', 'custom', 'MM/dd/yyyy h:mm a') ?? '' : '';

export const partyVerificationColDefs: ColDef[] = [
  {
    headerName: 'Party Name',
    field: 'partyName',
    filter: 'agTextColumnFilter',
    minWidth: 180,
    flex: 1.2,
    cellRenderer: 'partyNameRenderer',
  },

  {
    headerName: 'Contact Validation',
    field: 'cvStatus',
    cellRenderer: 'verificationChip',
    width: 180,
  },

  {
    headerName: 'ID Verification',
    field: 'idvStatus',
    cellRenderer: 'verificationChip',
    width: 180,
    tooltipValueGetter: (partyRow: ITooltipParams) => {
      const status = partyRow.value as string;
      const attemptsLeft = partyRow.data?.idvAttemptsLeft;

      if (status !== 'Expired') return null;

      if (attemptsLeft == null) return 'No attempts remaining';

      const label = attemptsLeft === 1 ? 'attempt remaining' : 'attempts remaining';
      return `${attemptsLeft} ${label}`;
    },
  },

  { headerName: 'Underwriter', field: 'underwriter', filter: 'agTextColumnFilter', minWidth: 200 },

  { headerName: 'File Number', field: 'fileNumber', filter: 'agTextColumnFilter', width: 150 },

  { headerName: 'Requested By', field: 'requestedByName', filter: 'agTextColumnFilter', minWidth: 160 },

  {
    headerName: 'Last Run',
    field: 'lastRun',
    valueFormatter: fmtDate,
    width: 190,
    filter: 'agDateColumnFilter',
    filterParams: filterParams,
    sort: 'desc',
  },
  {
    field: 'pdf',
    headerName: '',
    cellClass: 'center-align-column jacket-pdf-cell',
    cellRenderer: 'summaryRenderer',
    width: 40,
    minWidth: 40,
    filter: false,
    floatingFilter: false,
    pinned: 'right',
  },
];
